import { useEffect, useRef } from 'react';
import { Badge } from '../common/Badge';
import { Button } from '../common/Button';

interface ScreenShareViewProps {
  stream: MediaStream | null;
  sharerName: string;
  isLocal: boolean;
  onStopShare?: () => void;
}

/**
 * Full-screen view for screen share content.
 */
export function ScreenShareView({
  stream,
  sharerName,
  isLocal,
  onStopShare,
}: ScreenShareViewProps) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  if (!stream) {
    return null;
  }

  return (
    <div className="relative w-full h-full bg-surface rounded-xl overflow-hidden border border-border">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={isLocal}
        className="w-full h-full object-contain"
      />

      <div className="absolute top-4 left-4 flex items-center gap-2">
        <Badge variant="primary">
          <ScreenIcon />
          {sharerName}'s screen
        </Badge>
      </div>

      {isLocal && onStopShare && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
          <Button onClick={onStopShare} variant="danger" className="px-5">
            <StopIcon />
            Stop Sharing
          </Button>
        </div>
      )}
    </div>
  );
}

interface ScreenShareMinimizedProps {
  sharerName: string;
  onClick: () => void;
}

/**
 * Minimized screen share indicator.
 */
export function ScreenShareMinimized({ sharerName, onClick }: ScreenShareMinimizedProps) {
  return (
    <Button onClick={onClick} variant="secondary" className="px-4">
      <ScreenIcon />
      <span className="text-sm">{sharerName} is sharing</span>
      <ExpandIcon />
    </Button>
  );
}

/**
 * Icon for shared screen status.
 */
function ScreenIcon() {
  return (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/**
 * Icon for stopping a screen share.
 */
function StopIcon() {
  return (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/**
 * Icon for expanding a minimized screen share.
 */
function ExpandIcon() {
  return (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 11-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 012 0v1.586l2.293-2.293a1 1 0 111.414 1.414L6.414 15H8a1 1 0 010 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 111.414-1.414L15 13.586V12a1 1 0 011-1z"
        clipRule="evenodd"
      />
    </svg>
  );
}
